import { http } from '@/services/http'
import { storage, UserInactiveError } from '@/shared'
import type { User } from '@/types/user'

function assertUserActive(user: User): void {
  if (!user.active) {
    storage.clearAll()
    throw new UserInactiveError()
  }
}

interface LoginRequest {
  email: string
  password: string
}

interface LoginResponse {
  user: User
}

interface LoginTokenResponse {
  access_token: string
}

interface RegisterRequest {
  name: string
  email: string
  password: string
  tenantId?: string
}

interface ForgotPasswordRequest {
  email: string
}

interface ResetPasswordRequest {
  token: string
  password: string
}

export const authService = {
  login: async (payload: LoginRequest): Promise<LoginResponse> => {
    // API sets refresh_token as httpOnly cookie, returns only access_token in body
    const tokens = await http.post<LoginTokenResponse>('/auth/login', payload)

    storage.setToken(tokens.access_token)

    const response = await authService.me()

    assertUserActive(response.user)

    return response
  },

  logout: async (): Promise<void> => {
    try {
      await http.post<void>('/auth/logout')
    } finally {
      storage.clearAll()
    }
  },

  register: async (payload: RegisterRequest): Promise<LoginResponse> => {
    const tokens = await http.post<LoginTokenResponse>('/auth/register', payload)

    storage.setToken(tokens.access_token)

    const response = await authService.me()

    assertUserActive(response.user)

    return response
  },

  me: async (): Promise<{ user: User }> => {
    return http.get<{ user: User }>('/auth/me')
  },

  /**
   * Refresh access token via httpOnly cookie
   * Cookie is sent automatically with withCredentials: true
   * Returns new access_token in body, new refresh_token is set as cookie by API
   */
  refresh: async (): Promise<{ access_token: string }> => {
    return http.post<{ access_token: string }>('/auth/refresh')
  },

  forgotPassword: async (payload: ForgotPasswordRequest): Promise<void> => {
    return http.post<void>('/auth/forgot-password', payload)
  },

  validateResetToken: async (token: string): Promise<void> => {
    return http.get<void>(`/auth/validate-reset-token/${token}`)
  },

  resetPassword: async (payload: ResetPasswordRequest): Promise<void> => {
    return http.post<void>('/auth/reset-password', payload)
  },
}
